import { useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { uploadExpenseAttachments } from '@/api/services/expenses.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { DOCUMENT_UPLOAD_MAX_FILE_SIZE } from '@/features/documents/utils/documentUtils'
import type { ExpenseAttachment } from '@/features/expenses/types/expense.types'

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.avif'
const MAX_FILE_SIZE = DOCUMENT_UPLOAD_MAX_FILE_SIZE
const MAX_FILE_SIZE_LABEL = '20 MB'
const MAX_FILES = 5

function validateFiles(files: File[], existingCount: number): string | null {
  if (existingCount + files.length > MAX_FILES) return `Maximum ${MAX_FILES} attachments allowed`
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) return `"${file.name}" exceeds the ${MAX_FILE_SIZE_LABEL} limit`
  }
  return null
}

interface ExpenseAttachmentsProps {
  value: ExpenseAttachment[]
  onChange: (attachments: ExpenseAttachment[]) => void
  disabled?: boolean
}

/** Receipt/invoice uploader. Uploads immediately and returns attachment refs to the form. */
export function ExpenseAttachments({
  value,
  onChange,
  disabled,
}: ExpenseAttachmentsProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFiles = async (fileList: FileList | null): Promise<void> => {
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList)
    const validationError = validateFiles(files, value.length)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    setUploading(true)
    try {
      const uploaded = await uploadExpenseAttachments(files)
      onChange([...value, ...uploaded])
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const removeAt = (id: string): void => {
    onChange(value.filter((a) => a.id !== id))
  }

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Attachments
      </span>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
        disabled={disabled || uploading}
      />
      <Button
        type="button"
        variant="secondary"
        className="h-9 text-xs"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || value.length >= MAX_FILES}
        loading={uploading}
      >
        <Paperclip className="h-4 w-4" aria-hidden />
        <span className="ml-2">Add receipts</span>
      </Button>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        PDF, Office files, text files or images up to {MAX_FILE_SIZE_LABEL}. Max {MAX_FILES} files.
      </p>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((att) => (
            <li
              key={att.id}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800/50"
            >
              <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">
                {att.originalName || 'Receipt'}
              </span>
              <button
                type="button"
                onClick={() => removeAt(att.id)}
                className="shrink-0 rounded p-0.5 text-gray-400 hover:text-red-500"
                aria-label={`Remove ${att.originalName || 'attachment'}`}
                disabled={disabled}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

ExpenseAttachments.displayName = 'ExpenseAttachments'
